// Important: we are contractually obligated to mention the Solventum API.
// https://notablehealth.slack.com/archives/C05HGHZRQ5A/p1738863160218869?thread_ts=1738862065.145969&cid=C05HGHZRQ5A
export const medicalNecessityCheckDocumentationMarkdown = `
### What does this block do?

The Medical Necessity Check Block verifies that a procedure passes Medicare-specific edits regarding medical necessity. It is only applicable for patients with Medicare and uses the Solventum API to perform the check. The block evaluates the provided diagnosis ICD-10 codes and procedure CPT codes against Medicare's required edits to determine if a claim is likely to be accepted.

### When to use it?

- **Pre-Referral Entry Verification:** Before entering a referral order into the EHR for Medicare patients to ensure procedures meet necessary criteria.  
- **Billing Workflows:** Employ it to minimize claim rejections due to medical necessity issues.  
- **Treatment Validation:** Integrate it when validating that treatment plans comply with Medicare standards before service delivery.  
- **Flagging Procedures:** Use it to flag procedures that may need additional review or documentation prior to claim submission.

### Example Flows

1. **Pre-Referral Entry Verification:**  
  Before a referral order is entered into the EHR for a Medicare patient, run the Medical Necessity Check Block to ensure that the procedure meets Medicare-specific edits, reducing the risk of surprise bills down the road.

2. **Automated Billing Workflow:**  
  Automatically verify the medical necessity of procedures during the billing process, ensuring that only eligible claims are forwarded for processing.

3. **Clinical Decision Support:**  
  Use the block during patient scheduling to confirm that the proposed procedures comply with Medicare guidelines, thereby informing treatment planning.

4. **Quality Assurance:**  
  Integrate the block into regular audits of clinical procedures to ensure ongoing compliance with Medicare medical necessity requirements.

## How to Configure This Block

### **1. Insurance Provider**  
  - **Description:** Enter the Medicare insurance provider to which claims will be submitted.  
  - **Error Message:** "Insurance Provider is required"  
  - **Note:** The input must match one of the valid insurance providers listed below.  

#### Valid Insurance Providers:
\`\`\`
AK Part A
AK Part B
AL Part A
AL Part B
AR Part A
AR Part B
AZ Part A
AZ Part B
CA Part A
CA-Northern Part B
CA-Southern Part B
CA Part A (formerly Mutual of Omaha)
CO Part A
CO Part B
CO Part A (formerly Mutual of Omaha)
CT Part A
CT Part B
DC Part A (formerly Mutual of Omaha)
DC Part A
DC Part B
DE Part A (formerly Mutual of Omaha)
DE Part A
DE Part B
DMERC Region A
FL Part A
FL Part B
GA Part A
GA Part B
HI Part A
HI Part A (formerly Mutual of Omaha)
HI Part B
IA Part A
IA Part B
ID Part A
ID Part B
IL Part A
IL Part B
IN Part A
IN Part B
KS Part A
KS Part B
KY Part A
KY Part B
LA Part A
LA Part B
MA Part A
MA Part B
MD Part A (formerly Mutual of Omaha)
MD Part A
MD Part B
ME Part A
ME Part B
MI Part A
MI Part B
MN Part A
MN Part B
MO Part A
MO-Eastern Part B
MO-Western Part B
MS Part A
MS Part B
MT Part A
MT Part B
NC Part A
NC Part B
ND Part A
ND Part B
NE Part A
NE Part B
NH Part A
NH Part B
NJ Part A (formerly Mutual of Omaha)
NJ Part A
NJ Part B
NM Part A
NM Part B
NM Part A (formerly Mutual of Omaha)
NV Part A
NV Part A (formerly Mutual of Omaha)
NV Part B
NY Part A
NY-Northern Part B
NY-Queens Part B
NY-Southern Part B
OH Part A
OH Part B
OK Part A
OK Part B
OK Part A (formerly Mutual of Omaha)
OR Part A
OR Part B
PA Part A (formerly Mutual of Omaha)
PA Part A
PA Part B
PR Part A
PR Part B
RI Part A
RI Part B
SC Part A
SC Part B
SD Part A
SD Part B
TN Part A
TN Part B
TX Part A (includes IHS/Tribal Orgs and Veterans Affairs)
TX Part B (includes IHS/Tribal Orgs and Veterans Affairs)
TX Part A (formerly Mutual of Omaha)
UT Part A
UT Part B
VA Part A
VA Part B
VI Part A
VI Part B
VT Part A
VT Part B
WA Part A
WA Part B
WI Part A
WI Part B
WV Part A
WV Part B
WY Part A
WY Part B
Aetna
Anthem of VA
BCBS of MA
CA Medicaid
Mutual of Omaha
TX Medicaid (Children with Special Health Care Needs (CSHCN))
TX Medicaid (Texas Health Steps (THSteps))
TX Medicaid (Texas Medicaid & Healthcare Partnership (TMHP))
\`\`\`

### **2. Diagnosis ICD-10 Codes**  
- **Description:** Enter a list of ICD-10 codes formatted as a JSON array.  
- **Error Message:** "Diagnosis Codes is required"  
- **Example:** \`["U071", "C43.4"]\`

### **3. Procedure CPT Codes**  
- **Description:** Enter a list of CPT codes for the procedures being performed, formatted as a JSON array.  
- **Error Message:** "Procedure CPT Codes is required"  
- **Example:** \`["75625", "75432"]\`

### **4. Procedure Service Date**  
- **Description:** Enter the service date in YYYY-MM-DD format.  
- **Helper Text:** "Provide date in YYYY-MM-DD format, example: 2024-11-02"  
- **Default:** If not provided, the block defaults to today's date.

### **5. Mock Output for Test Run**  
- **Description:** Toggle the switch to enable or disable mock output.  
- **Helper Text:** "When enabled, this block will skip running during a test run and output the data below."

### **6. Save Your Configuration**  
After configuring all fields, click Save to update the block in your workflow.

## Configuration Examples

### Example 1: Basic Configuration
~~~
1. Configure Insurance Provider:
  - Enter: "CA Part A"
2. Configure Diagnosis ICD-10 Codes:
  - Enter: ["U071", "C43.4"]
3. Configure Procedure CPT Codes:
  - Enter: ["75625", "75432"]
4. Configure Procedure Service Date:
  - Enter: "2024-11-02"
~~~

### Example 2: Testing Configuration
~~~
1. Enable Mock Output for Test Run:
  - Toggle: "On"
2. Configure the Insurance Provider, Diagnosis ICD-10 Codes, Procedure CPT Codes, and Procedure Service Date as in Example 1.
~~~

## How to Test

### 1. Prepare the Environment

- Ensure that all required fields are populated with valid data.

### 2. Simulate a Test Run

- Enable "Mock Output for Test Run" to review the output without making a live API call.

### 3. Execute the Flow

- Run the workflow and verify that the block returns an output object containing "status" and "details".

### 4. Error Handling

- Test with missing or invalid inputs to confirm that appropriate error messages are displayed.

## Output

### **Status**
- "success" - All procedures passed the medical necessity check.  
- "failed" - At least one procedure failed the check due to rejection or invalid data.  
- "warning" - Procedures passed with warnings that may require future adjustments.

### **Details**
Contains detailed information about the medical necessity check, useful for troubleshooting.

#### Sample Output:
\`\`\`json
{
  "status": "success",
  "details": {
    "responseList": [
      {
        "claimOutputList": [
          {
            "fields": {
              "itemList": [
                {
                  "procedure": {
                    "code": "77066",
                    "codeDescription": "Dx mammo incl cad bi"
                  }
                }
              ]
            }
          }
        ]
      }
    ]
  }
}
\`\`\`

## Best Practices

1. **Verify Insurance Provider:**  
  Ensure the Insurance Provider input matches one of the valid providers listed above.

2. **Accurate Code Entry:**  
  Enter diagnosis and procedure codes as properly formatted JSON arrays to avoid errors.

3. **Date Accuracy:**  
  Use the correct date format (YYYY-MM-DD) and default to today's date if the service date is not known.

4. **Mock Testing:**  
  Utilize the Mock Output option to test configurations without triggering live API calls.

5. **Review Output Details:**  
  Examine the "details" property for insights and troubleshooting if the check fails.

6. **API Integration:**  
  Confirm that the connection to the Solventum API is active and that any required authentication is correctly configured.
`;
